import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Brain, Shield, Eye, BookOpen, Moon, Leaf, Scale, Atom, ExternalLink } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const monoFont = {
  fontFamily: '"JetBrains Mono", "Fira Code", "Monaco", Menlo, Consolas, monospace',
  fontWeight: 400,
} as const

const APIS = [
  {
    icon: Brain,
    name: 'MATRIZ Reasoning',
    base: '/v1/reasoning',
    badge: 'stable',
    desc: 'Submit queries and receive fully traced reasoning graphs with node-level confidence scores.',
    endpoints: [
      { method: 'POST', path: '/v1/reasoning/query', desc: 'Submit a reasoning query' },
      { method: 'GET',  path: '/v1/reasoning/:id',   desc: 'Retrieve a saved reasoning result' },
      { method: 'POST', path: '/v1/reasoning/batch',  desc: 'Submit multiple queries' },
    ],
  },
  {
    icon: Shield,
    name: 'Guardian Ethics',
    base: '/v1/guardian',
    badge: 'stable',
    desc: 'Constitutional AI compliance checks. Returns pass/fail, violations, and remediation suggestions.',
    endpoints: [
      { method: 'POST', path: '/v1/guardian/check',     desc: 'Check content against policy' },
      { method: 'GET',  path: '/v1/guardian/policies',  desc: 'List available ethical policies' },
      { method: 'GET',  path: '/v1/guardian/audit/:id', desc: 'Retrieve audit trail entry' },
    ],
  },
  {
    icon: Eye,
    name: 'Identity (ΛiD)',
    base: '/v1/identity',
    badge: 'stable',
    desc: 'Tiered authentication, passkey registration, JWT issuance, and namespace-scoped access tokens.',
    endpoints: [
      { method: 'POST', path: '/v1/identity/token',     desc: 'Issue an access token' },
      { method: 'POST', path: '/v1/identity/passkey',   desc: 'Register a passkey' },
      { method: 'GET',  path: '/v1/identity/me',        desc: 'Fetch current identity claims' },
    ],
  },
  {
    icon: BookOpen,
    name: 'Memory Fold',
    base: '/v1/memory',
    badge: 'stable',
    desc: 'Symbolic memory store with cascade-safe fold architecture. Full provenance on every fold.',
    endpoints: [
      { method: 'POST',   path: '/v1/memory/fold',       desc: 'Create a memory fold' },
      { method: 'GET',    path: '/v1/memory/fold/:id',   desc: 'Retrieve a fold by ID' },
      { method: 'DELETE', path: '/v1/memory/fold/:id',   desc: 'Soft-delete a fold (consent erasure)' },
    ],
  },
  {
    icon: Moon,
    name: 'Dream Engine',
    base: '/v1/dream',
    badge: 'beta',
    desc: 'Oneiric synthesis for creative and associative reasoning. Generative loops with Guardian oversight.',
    endpoints: [
      { method: 'POST', path: '/v1/dream/generate', desc: 'Start a dream synthesis session' },
      { method: 'GET',  path: '/v1/dream/:id',      desc: 'Poll session status and output' },
    ],
  },
  {
    icon: Leaf,
    name: 'Bio Adaptation',
    base: '/v1/bio',
    badge: 'beta',
    desc: 'Bio-inspired attention rhythms. Adaptive signal weighting for context-sensitive routing.',
    endpoints: [
      { method: 'POST', path: '/v1/bio/signal',  desc: 'Submit a bio signal for weighting' },
      { method: 'GET',  path: '/v1/bio/rhythm',  desc: 'Retrieve current attention rhythm state' },
    ],
  },
  {
    icon: Scale,
    name: 'Consciousness Bus',
    base: '/v1/bus',
    badge: 'stable',
    desc: 'Pub-sub context routing between Constellation nodes. Sub-250ms handoff SLA.',
    endpoints: [
      { method: 'POST', path: '/v1/bus/publish',      desc: 'Publish a context event' },
      { method: 'POST', path: '/v1/bus/subscribe',    desc: 'Subscribe to a context topic' },
      { method: 'GET',  path: '/v1/bus/status',       desc: 'Check bus health and latency' },
    ],
  },
  {
    icon: Atom,
    name: 'Quantum Compute',
    base: '/v1/quantum',
    badge: 'experimental',
    desc: 'Quantum-inspired superposition planning and post-quantum cryptographic primitives.',
    endpoints: [
      { method: 'POST', path: '/v1/quantum/plan',    desc: 'Submit a superposition planning request' },
      { method: 'POST', path: '/v1/quantum/pqc/sign', desc: 'Sign a payload with post-quantum key' },
    ],
  },
]

const METHOD_STYLES: Record<string, string> = {
  GET:    'bg-vision-teal/15 text-vision-teal border-vision-teal/30',
  POST:   'bg-info-blue/15 text-info-blue border-info-blue/30',
  DELETE: 'bg-error-red/15 text-error-red border-error-red/30',
}

const BADGE_STYLES: Record<string, string> = {
  stable:       'bg-vision-teal/15 text-vision-teal border-vision-teal/30',
  beta:         'bg-lambda-gold/15 text-lambda-gold border-lambda-gold/30',
  experimental: 'bg-white/10 text-white/50 border-white/20',
}

export default function ApiReferencePage() {
  return (
    <div className="min-h-screen bg-consciousness-deep text-awareness-silver">
      <SiteHeader />

      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.09) 0%, transparent 70%)',
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-vision-teal/10 border border-vision-teal/25 mb-6">
              <span className="text-vision-teal text-xs tracking-[0.15em] uppercase" style={bodyFont}>
                API Reference
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl text-white tracking-[0.12em] mb-4" style={brandFont}>
              Constellation <span className="text-vision-teal">APIs</span>
            </h1>
            <p className="text-xl text-white/55 max-w-2xl" style={bodyFont}>
              Base URL:{' '}
              <code className="text-vision-teal bg-vision-teal/8 px-2 py-0.5 rounded text-lg" style={monoFont}>
                https://api.lukhas.io
              </code>
              <br />
              <span className="text-base">
                All endpoints accept JSON. Authentication via{' '}
                <code className="text-white/60 text-sm" style={monoFont}>
                  Authorization: Bearer &lt;token&gt;
                </code>
                . Public endpoints work without auth.
              </span>
            </p>
            <div className="mt-6 flex gap-3">
              <Link
                to="/playground"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-vision-teal/15 border border-vision-teal/30 text-vision-teal text-xs hover:bg-vision-teal/25 transition-all"
                style={bodyFont}
              >
                Try Playground
              </Link>
              <a
                href="https://lukhas.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-white/50 text-xs hover:border-white/30 hover:text-white/70 transition-all"
                style={bodyFont}
              >
                Full Docs <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 px-6 pb-24">
        <div className="max-w-4xl mx-auto space-y-8">
          {APIS.map(({ icon: Icon, name, base, badge, desc, endpoints }, i) => (
            <motion.div
              key={name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04, ease: [0.22, 1, 0.32, 1] }}
              className="rounded-2xl border border-white/[0.08] bg-white/[0.025] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-start gap-4 p-6 border-b border-white/[0.06]">
                <div className="p-2 rounded-lg bg-vision-teal/10 shrink-0">
                  <Icon className="w-5 h-5 text-vision-teal" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-lg text-white tracking-wide" style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 300 }}>
                      {name}
                    </h2>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border tracking-[0.1em] uppercase ${BADGE_STYLES[badge]}`}
                      style={bodyFont}
                    >
                      {badge}
                    </span>
                  </div>
                  <code className="text-xs text-vision-teal/60" style={monoFont}>
                    {base}
                  </code>
                  <p className="text-sm text-white/45 mt-2" style={bodyFont}>
                    {desc}
                  </p>
                </div>
              </div>

              {/* Endpoints */}
              <div className="divide-y divide-white/[0.05]">
                {endpoints.map(({ method, path, desc: epDesc }) => (
                  <div key={path} className="flex items-center gap-4 px-6 py-3 hover:bg-white/[0.02] transition-colors">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded border w-14 text-center tracking-[0.05em] shrink-0 ${METHOD_STYLES[method]}`}
                      style={monoFont}
                    >
                      {method}
                    </span>
                    <code className="text-xs text-white/65 flex-1" style={monoFont}>
                      {path}
                    </code>
                    <span className="text-xs text-white/35 hidden sm:block" style={bodyFont}>
                      {epDesc}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
